import { Link } from "wouter";
import { AlertTriangle, CheckCircle2, ExternalLink, Info, Loader2, Siren } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { DraperConversationMessage } from "@shared/schema";
import { normalizeEscalations, type Escalation, type EscalationLevel } from "@/hooks/use-escalations";
import { DeliveryLine, LevelChip } from "@/components/escalation-ui";

/**
 * Status notes in the Draper thread.
 *
 * The server posts short notes as the Claude team and the autopublisher work: "⏳ #12 will publish on Tue 9:00 unless you
 * hold it", "✅ Peggy finished: ...", "⚠️ Peggy couldn't finish ...", "✅ Plan approved ...". They start with a status emoji
 * and usually carry metadata ({kind, event, contentItemId, planId, items}). They are drawn as a card with a link to the
 * thing they are about instead of as a chat bubble.
 */

export type StatusTone = "progress" | "done" | "warn" | "info" | "alarm";

export interface StatusNote {
  tone: StatusTone;
  /** First line, without the leading emoji. */
  headline: string;
  /** Everything after the first line (markdown). */
  body: string;
  /** Content items the note is about (open in Content Studio). */
  itemIds: number[];
  /** True when the note is about a content plan (links to the Plan tab). */
  aboutPlan: boolean;
  /** Set on an escalation note (metadata.kind "escalation"): how urgent it is, and what we know about delivery. */
  escalation?: { level: EscalationLevel; delivery: Escalation | null };
}

// Emoji are matched by code point (an editor or tool that drops the invisible variation selector U+FE0F cannot break this):
// 0x23F3 hourglass, 0x2705 check, 0x26A0 warning, 0x1F680 rocket (published), 0x1F6A8 siren (live but failing),
// 0x23F8 pause, 0x1F5D3 calendar, 0x2699 gear.
const TONE_BY_CODE: Record<number, StatusTone> = {
  0x23f3: "progress",
  0x2705: "done",
  0x26a0: "warn",
  0x1f680: "done",
  0x1f6a8: "warn",
  0x23f8: "info",
  0x1f5d3: "info",
  0x2699: "info",
};
/**
 * Notes older than metadata.kind carry no marker, but the work-order notes have a fixed first line: "<emoji> Peggy finished: ..."
 * and "<emoji> Peggy couldn't finish ...". Those still count. An ordinary Draper answer that merely opens with a tick (the Gemini
 * chat does this a lot) is NOT a status note and stays a chat bubble.
 */
const LEGACY_NOTE_LINE = /^\S+\s+\S+ (finished:|couldn't finish )/;
const KINDS = new Set(["agentic", "work_complete", "work_failed", "escalation"]);

function asId(v: unknown): number | null {
  const n = typeof v === "string" && /^\d+$/.test(v) ? Number(v) : v;
  return typeof n === "number" && Number.isInteger(n) && n > 0 ? n : null;
}

/** The status emoji a message opens with (and the text after it), if any. */
function leadEmoji(text: string): { code: number; rest: string } | null {
  const t = text.trimStart();
  const code = t.codePointAt(0);
  if (code === undefined || !(code in TONE_BY_CODE)) return null;
  let i = String.fromCodePoint(code).length;
  if (t.charCodeAt(i) === 0xfe0f) i += 1; // emoji variation selector
  return { code, rest: t.slice(i).trimStart() };
}

/** null when the message is an ordinary chat bubble. */
export function parseStatusNote(msg: Pick<DraperConversationMessage, "role" | "text" | "richCard" | "batchCard" | "metadata">): StatusNote | null {
  if (msg.role === "user" || msg.richCard) return null;
  const text = String(msg.text || "");
  const lead = leadEmoji(text);
  const meta = (msg.metadata || {}) as Record<string, any>;
  const kind = typeof meta.kind === "string" ? meta.kind : "";
  const fromServer = KINDS.has(kind);

  const legacy = !!lead && LEGACY_NOTE_LINE.test(text.trimStart().split("\n")[0]);
  if (!(fromServer || legacy)) return null;

  const rest = lead ? lead.rest : text.trim();
  const nl = rest.indexOf("\n");
  let headline = (nl === -1 ? rest : rest.slice(0, nl)).trim();
  const body = nl === -1 ? "" : rest.slice(nl + 1).trim();

  // An escalation is an alarm, not a progress note: red, with its level and (when the server says) how delivery went.
  let escalation: StatusNote["escalation"];
  if (kind === "escalation") {
    const word = String(meta.level ?? meta.severity ?? "").trim().toUpperCase();
    const fromText = /^[^A-Za-z0-9]*P([01])\b/i.exec(headline);
    const level: EscalationLevel = word === "P0" || word === "P1" ? word : word === "INFO" ? "info" : fromText ? (`P${fromText[1]}` as EscalationLevel) : "P0";
    // The chip carries the level, so a "P0:" the sender typed into the headline is not said twice.
    headline = headline.replace(/^[^A-Za-z0-9]*P[01]\s*[:\-—]\s*/i, "") || headline;
    const delivery = Array.isArray(meta.channels) && meta.channels.length ? normalizeEscalations([{ id: 0, level, title: headline, channels: meta.channels }])[0] ?? null : null;
    escalation = { level, delivery };
  }
  const tone: StatusTone = escalation
    ? escalation.level === "info" ? "info" : "alarm"
    : (lead && TONE_BY_CODE[lead.code]) || (kind === "work_failed" ? "warn" : kind === "work_complete" ? "done" : "info");

  const planId = asId(meta.planId);
  // A finished plan or plan review is about a plan, not a content item.
  const aboutPlan = planId !== null || /plan/i.test(String(meta.deliverable || "")) || /^plan_/.test(String(meta.event || ""));
  const ids: number[] = [];
  const add = (v: unknown) => {
    const id = asId(v);
    if (id !== null && !ids.includes(id)) ids.push(id);
  };
  add(meta.contentItemId);
  add(meta.itemId);
  if (Array.isArray(meta.itemIds)) meta.itemIds.forEach(add);
  if (Array.isArray(meta.items)) meta.items.forEach((i: any) => add(i?.id));
  if (Array.isArray(msg.batchCard?.data?.items)) msg.batchCard!.data.items.forEach((i) => add(i.id));
  // "#123" in the text is a content item, except in notes about a plan ("Replaced plan #3").
  if (!aboutPlan && ids.length === 0) for (const m of text.matchAll(/#(\d{1,9})\b/g)) add(m[1]);

  return { tone, headline, body, itemIds: ids.slice(0, 12), aboutPlan, ...(escalation ? { escalation } : {}) };
}

const TONE: Record<StatusTone, { box: string; icon: JSX.Element; label: string }> = {
  progress: {
    box: "border-cyan-500/25 bg-cyan-500/[0.06]",
    icon: <Loader2 size={14} className="animate-spin text-cyan-400" />,
    label: "In progress",
  },
  done: { box: "border-emerald-500/25 bg-emerald-500/[0.06]", icon: <CheckCircle2 size={14} className="text-emerald-400" />, label: "Done" },
  warn: { box: "border-amber-500/30 bg-amber-500/[0.07]", icon: <AlertTriangle size={14} className="text-amber-400" />, label: "Needs attention" },
  alarm: { box: "border-red-500/60 bg-red-500/[0.16]", icon: <Siren size={14} className="text-red-400" />, label: "Urgent" },
  info: { box: "border-white/10 bg-white/[0.04]", icon: <Info size={14} className="text-white/50" />, label: "Update" },
};

const SHOWN_LINKS = 3;

export function StatusNoteCard({ note }: { note: StatusNote }) {
  const t = TONE[note.tone];
  const shown = note.itemIds.slice(0, SHOWN_LINKS);
  const more = note.itemIds.length - shown.length;
  const linkClass =
    "inline-flex items-center gap-1 rounded-lg border border-white/15 bg-white/[0.06] px-2.5 py-1 text-[11px] font-semibold text-white/80 hover:bg-white/10 hover:text-white";
  return (
    <div className={`rounded-2xl rounded-tl-sm border px-4 py-3 ${t.box}`} data-testid="status-note-card" data-tone={note.tone} data-level={note.escalation?.level}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0" aria-label={t.label}>{t.icon}</span>
        <p className={`min-w-0 break-words text-sm font-semibold leading-snug ${note.tone === "alarm" ? "text-red-50" : "text-white/90"}`} data-testid="status-note-headline">
          {note.escalation && <LevelChip level={note.escalation.level} className="mr-2 align-middle" />}
          {note.headline}
        </p>
      </div>
      {note.escalation?.delivery && (
        <p className="mt-1.5 text-xs text-red-100/85" data-testid="status-note-delivery">
          <DeliveryLine escalation={note.escalation.delivery} />
        </p>
      )}
      {note.body && (
        <div className="prose prose-sm prose-invert mt-1.5 max-w-none break-words prose-p:my-1 prose-p:text-white/65 prose-ul:my-1 prose-li:my-0 prose-li:text-white/65 prose-a:text-blue-400 prose-strong:text-white/85 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{note.body}</ReactMarkdown>
        </div>
      )}
      {(shown.length > 0 || note.aboutPlan) && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="status-note-links">
          {shown.map((id) => (
            <Link key={id} href={`/content?tab=review&item=${id}`} className={linkClass} data-testid={`link-status-view-${id}`}>
              {note.itemIds.length === 1 ? "View" : `View #${id}`} <ExternalLink size={10} />
            </Link>
          ))}
          {more > 0 && (
            <Link href="/content?tab=review" className={linkClass} data-testid="link-status-view-more">
              +{more} more in Needs your review
            </Link>
          )}
          {note.aboutPlan && (
            <Link href="/content?tab=plan" className={linkClass} data-testid="link-status-view-plan">
              View plan <ExternalLink size={10} />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
